// AUTO-GENERATED FILE - DO NOT EDIT
// Generated by scripts/generate-cube-meta.ts
import type {
  CubeMeasureEntry,
  CubeDimensionEntry,
  CubeJoinEntry
} from 'shared/src/api/cube-api-types';

export const platformEntityChangesDailyCubeMeta = {
  name: 'platform_entity_changes_daily',
  sqlTable: 'metrics.platform_entity_changes_daily',
  title: 'Platform Entity Changes Daily',
  description: 'Daily grain change metrics per platform entity per field. Use for time-series.',
  joins: {
    platform_entities: { relationship: 'many_to_one' } as const satisfies CubeJoinEntry
  },
  measures: {
    'platform_entity_changes_daily.change_count': {
      sql: 'change_count',
      type: 'number',
      aggType: 'sum',
      title: 'Change Count',
      description: 'Total number of field changes. SUM across entities/days/fields.',
      meta: {
        category: 'changelog',
        grain: 'daily',
        supports_rollup: true,
        formula: 'SUM(change_count)',
        inputs: [],
        short_description: 'Total field changes across filtered rows',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'platform_entity_changes_daily.distinct_fields_changed': {
      sql: 'field_path',
      type: 'number',
      aggType: 'countDistinct',
      title: 'Distinct Fields Changed',
      description: 'Number of unique fields that changed.',
      meta: {
        category: 'changelog',
        grain: 'daily',
        supports_rollup: false,
        formula: 'COUNT(DISTINCT field_path)',
        inputs: ['platform_entity_changes_daily.field_path'],
        short_description: 'Number of unique fields that changed',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'platform_entity_changes_daily.distinct_entities_changed': {
      sql: 'platform_entity_id',
      type: 'number',
      aggType: 'countDistinct',
      title: 'Distinct Entities Changed',
      description: 'Number of unique entities with changes.',
      meta: {
        category: 'changelog',
        grain: 'daily',
        supports_rollup: false,
        formula: 'COUNT(DISTINCT platform_entity_id)',
        inputs: ['platform_entity_changes_daily.platform_entity_id'],
        short_description: 'Number of unique entities with changes',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'platform_entity_changes_daily.distinct_days_with_changes': {
      sql: 'day',
      type: 'number',
      aggType: 'countDistinct',
      title: 'Days With Changes',
      description: 'Number of distinct days with changes.',
      meta: {
        category: 'changelog',
        grain: 'overall',
        supports_rollup: false,
        formula: 'COUNT(DISTINCT day)',
        inputs: ['platform_entity_changes_daily.day'],
        short_description: 'Number of distinct days with changes',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeMeasureEntry
  },
  dimensions: {
    'platform_entity_changes_daily.id': {
      sql: "platform_entity_changes_daily.platform_entity_id || '-' || platform_entity_changes_daily.day::text || '-' || platform_entity_changes_daily.field_path || '-' || platform_entity_changes_daily.change_type",
      type: 'string',
      title: 'Id',
      primaryKey: true,
      meta: {
        short_description: 'Composite key (entity_id + day + field_path)',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entity_changes_daily.platform_entity_id': {
      sql: 'platform_entity_id',
      type: 'string',
      title: 'Entity ID',
      meta: {
        short_description: 'Parent platform entity identifier',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entity_changes_daily.day': {
      sql: 'day',
      type: 'time',
      title: 'Day',
      meta: {
        short_description: 'Calendar date for this change record',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entity_changes_daily.field_path': {
      sql: 'field_path',
      type: 'string',
      title: 'Field',
      description: 'Top-level key from the entity data envelope that changed.',
      meta: {
        short_description: 'Field that changed (top-level key of raw_entity data)',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entity_changes_daily.change_category': {
      sql: 'change_category',
      type: 'string',
      title: 'Change Category',
      description:
        "'manual' (user-initiated: bid_price, budget, active) or 'automated' (platform-managed: impressions, spend, updated_at).",
      meta: {
        short_description: "'manual' or 'automated' field classification",
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entity_changes_daily.media_platform_id': {
      sql: 'media_platform_id',
      type: 'string',
      title: 'Platform ID',
      meta: {
        short_description: 'Media platform ID (denormalized for filtering)',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entity_changes_daily.entity_type': {
      sql: 'entity_type',
      type: 'string',
      title: 'Entity Type',
      meta: {
        short_description: 'Entity type (denormalized for filtering)',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entity_changes_daily.change_type': {
      sql: 'change_type',
      type: 'string',
      title: 'Change Type',
      description:
        "Whether this change represents entity creation ('created') or an ongoing update ('updated')",
      meta: {
        short_description: "'created' or 'updated' event classification",
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'platform_entity_changes_daily.changeset_id': {
      sql: 'changeset_id',
      type: 'string',
      title: 'Changeset ID',
      description:
        'MAX(changeset_id) across all changesets contributing to this row. Used for HWM derivation and tracing.',
      meta: {
        short_description: 'Latest changeset that affected this row (HWM + tracing)',
        source_tables: ['metrics.platform_entity_changes_daily']
      }
    } as const satisfies CubeDimensionEntry
  }
};
